import { createApp } from 'vue';
import Oruga from '@oruga-ui/oruga-next';
import * as Sentry from '@sentry/vue';
import { MotionPlugin } from '@vueuse/motion';
import FloatingVue from 'floating-vue';
import Toast, { PluginOptions, POSITION } from 'vue-toastification';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import VueDatePicker from '@vuepic/vue-datepicker';

import 'unfonts.css';
import 'animate.css';
import './style.css';
import 'floating-vue/dist/style.css';
import 'vue-toastification/dist/index.css';
import 'vue3-perfect-scrollbar/style.css';
import '@oruga-ui/oruga-next/dist/oruga-full-vars.min.css';

import App from './App.vue';

import { getAuth } from '@firebase/auth';
import { initFirebase } from './firebase';
import firebaseConfig from './firebaseConfig';
import { createI18n } from './i18n';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faGoogle } from '@fortawesome/free-brands-svg-icons/faGoogle';
import { faAngleLeft } from '@fortawesome/pro-light-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/pro-light-svg-icons/faAngleRight';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faCircleNotch } from '@fortawesome/pro-light-svg-icons/faCircleNotch';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons/faExclamationCircle';
import { faEye } from '@fortawesome/pro-light-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash';
import { faHouse } from '@fortawesome/pro-light-svg-icons/faHouse';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faSpinner } from '@fortawesome/pro-light-svg-icons/faSpinner';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons/faPenToSquare';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faDownload } from '@fortawesome/pro-light-svg-icons/faDownload';
import { faAngleUp } from '@fortawesome/pro-light-svg-icons/faAngleUp';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons/faAngleDown';
import { faBarsFilter } from '@fortawesome/pro-light-svg-icons/faBarsFilter';
import { faFilter } from '@fortawesome/pro-light-svg-icons/faFilter';
import { faFilter as fasFilter } from '@fortawesome/pro-solid-svg-icons/faFilter';
import { faFilterSlash } from '@fortawesome/pro-light-svg-icons/faFilterSlash';
import { faArrowLeftFromLine } from '@fortawesome/pro-light-svg-icons/faArrowLeftFromLine';
import { faArrowRightFromLine } from '@fortawesome/pro-light-svg-icons/faArrowRightFromLine';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faBoxOpen } from '@fortawesome/pro-light-svg-icons/faBoxOpen';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons/faCircleCheck';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons/faCalendarDays';
import { faListUl } from '@fortawesome/pro-light-svg-icons/faListUl';
import { faChartLine } from '@fortawesome/pro-light-svg-icons/faChartLine';
import { faCirclePlus } from '@fortawesome/pro-light-svg-icons/faCirclePlus';
import { faCircleEllipsis } from '@fortawesome/pro-light-svg-icons/faCircleEllipsis';
import { faPipeValve } from '@fortawesome/pro-light-svg-icons';
import { faArrowRotateRight } from '@fortawesome/pro-light-svg-icons';
import { faArrowRightFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowRightFromBracket';
import { faGrid } from '@fortawesome/pro-light-svg-icons/faGrid';
import { faWebhook } from '@fortawesome/pro-light-svg-icons/faWebhook';
import { faClock } from '@fortawesome/pro-light-svg-icons';
import { faMinus } from '@fortawesome/pro-light-svg-icons';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { faAdd } from '@fortawesome/pro-light-svg-icons/faAdd';
import { faListTree } from '@fortawesome/pro-light-svg-icons/faListTree';
import { faLocationCrosshairs } from '@fortawesome/pro-light-svg-icons/faLocationCrosshairs';
import { faMemoCircleInfo } from '@fortawesome/pro-light-svg-icons/faMemoCircleInfo';
import { faCircleShareNodes } from '@fortawesome/pro-light-svg-icons';
import { faQuestion } from '@fortawesome/pro-light-svg-icons';
import { faHeart } from '@fortawesome/pro-light-svg-icons/faHeart';
import { faGaugeHigh } from '@fortawesome/pro-light-svg-icons/faGaugeHigh';
import { faBandage } from '@fortawesome/pro-light-svg-icons/faBandage';
import { faGear } from '@fortawesome/pro-light-svg-icons/faGear';
import { faStar } from '@fortawesome/pro-light-svg-icons/faStar';
import { faStar as fasStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faLayerGroup } from '@fortawesome/pro-light-svg-icons/faLayerGroup';
import { faLayerGroup as fasLayerGroup } from '@fortawesome/pro-solid-svg-icons/faLayerGroup';
import { faArrowsUpDown } from '@fortawesome/pro-light-svg-icons/faArrowsUpDown';
import { faScrewdriverWrench } from '@fortawesome/pro-light-svg-icons/faScrewdriverWrench';
import { faTrashCanList } from '@fortawesome/pro-light-svg-icons/faTrashCanList';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { VueQueryPlugin } from '@tanstack/vue-query';
import * as validators from '@vuelidate/validators';

import HField from '@/components/base/HField.vue';
import PasswordMeter from 'vue-simple-password-meter';
import pinia from '@/store';
import router from '@/router';
import acl from '@/acl';
import authGuard from '@/router/guards';
import type { ErrorEvent, EventHint } from '@sentry/types/build/types/event';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getAuthProviderName } from './auth/AuthFactory';

declare const __APP_VERSION__: string;

let app: ReturnType<typeof createApp>;
const authMode = import.meta.env.VITE_AUTH_MODE;
export const i18n = createI18n();
const { createI18nMessage } = validators;
const { setPageTitle } = usePageTitle();

export const withI18nMessage = createI18nMessage({ t: i18n.global.t.bind(i18n) });

library.add([
  faEye,
  faEyeSlash,
  faGoogle,
  faExclamationCircle,
  faCircleNotch,
  faTimes,
  faSpinner,
  faSpinnerThird,
  faUser,
  faAngleLeft,
  faAngleRight,
  faMagnifyingGlass,
  faCheck,
  faHouse,
  faChevronDown,
  faPenToSquare,
  faTrashCan,
  faXmark,
  faDownload,
  faAngleUp,
  faAngleDown,
  faBarsFilter,
  faFilter,
  faFilterSlash,
  faCircleCheck,
  faArrowLeftFromLine,
  faArrowRightFromLine,
  faCopy,
  faBoxOpen,
  faTriangleExclamation,
  faArrowLeft,
  faCalendarDays,
  faListUl,
  faChartLine,
  faCirclePlus,
  faCircleEllipsis,
  faPipeValve,
  faArrowRotateRight,
  faArrowRightFromBracket,
  faGrid,
  faWebhook,
  faClock,
  faMinus,
  faLock,
  faAdd,
  faListTree,
  faLocationCrosshairs,
  faMemoCircleInfo,
  faCircleShareNodes,
  faQuestion,
  fasFilter,
  faHeart,
  faGaugeHigh,
  faBandage,
  faGear,
  faStar,
  fasStar,
  faLayerGroup,
  fasLayerGroup,
  faArrowsUpDown,
  faScrewdriverWrench,
  faTrashCanList,
  faArrowUpRightFromSquare,
  faCircleInfo,
]);

const initializeSentry = (app: ReturnType<typeof createApp>) => {
  Sentry.init({
    app,
    enabled: import.meta.env.PROD,
    dsn: import.meta.env.VITE_SENTRY_URL,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    integrations: [
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    logErrors: true,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 0.25,
    profilesSampleRate: 1.0,
    replaysSessionSampleRate: 0.25,
    replaysOnErrorSampleRate: 1.0,

    // Ignore specific errors using beforeSend
    beforeSend(event: ErrorEvent, hint: EventHint) {
      const error = hint.originalException;
      if (error && error.message) {
        if (
          // bunch of network errors
          error.message.includes('UNKNOWN: Transport error') ||
          error.message.includes('Failed to fetch dynamically imported module') ||
          error.message.includes('Unable to preload CSS') ||
          error.message.includes('Importing a module script failed') ||
          error.message.includes('error loading dynamically imported module') ||
          error.message.includes('AbortError') ||
          error.message.includes('UNAVAILABLE') ||
          // missing permission
          // working as expected
          error.message.includes('PERMISSION_DENIED') ||
          // session expired
          error.message.includes('UNAUTHENTICATED') ||
          // user error
          error.message.includes('INVALID_ARGUMENT') ||
          // firebase network issue
          // example: https://hiberglobal.sentry.io/issues/6499065497/?project=4506065967644672
          error.message.includes('auth/network-request-failed') ||
          // Firebase rate limiting, not actionable
          // example: https://hiberglobal.sentry.io/issues/4632771631/?project=4506065967644672
          error.message.includes('auth/too-many-requests') ||
          // no idea what these errors are, not actionable
          // example: https://hiberglobal.sentry.io/issues/6499065489/?project=4506065967644672
          error.message.includes('/static/index-')
        ) {
          return null;
        }
      }
      return event;
    },
    // see above for explanation for each
    ignoreErrors: [
      'UNKNOWN: Transport error',
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      'Unable to preload CSS',
      'error loading dynamically imported module',
      'AbortError',
      'UNAVAILABLE',
      'PERMISSION_DENIED',
      'UNAUTHENTICATED',
      'INVALID_ARGUMENT',
      'auth/network-request-failed',
      'auth/too-many-requests',
      '/static/index-',
    ],
  });
};

const initApp = () => {
  setPageTitle();
  app = createApp(App);
  if (import.meta.env.PROD) {
    initializeSentry(app);
  }

  router.beforeEach(authGuard);

  app.use(router);
  app.use(FloatingVue, {
    distance: 10,
    themes: {
      tooltip: {
        delay: { show: 100, hide: 200 },
      },
      'org-switcher': {
        $extend: 'dropdown',
        triggers: ['click'],
        autoHide: true,
        placement: 'bottom',
      },
      'actions-dropdown': {
        $extend: 'dropdown',
        triggers: ['click'],
        autoHide: true,
        placement: 'bottom',
      },
      'checkbox-dropdown': {
        $extend: 'dropdown',
        triggers: ['click'],
        autoHide: true,
        placement: 'bottom',
      },
    },
  });
  app.use(MotionPlugin);
  app.use(Toast, {
    newestOnTop: true,
    icon: false,
    position: POSITION.TOP_RIGHT,
  } as PluginOptions);
  app.use(PerfectScrollbarPlugin, {
    options: {
      suppressScrollX: true,
    },
  });
  app.use(i18n);
  app.use(pinia);
  app.use(VueQueryPlugin);
  app.use(acl);
  app.use(Oruga, {
    iconPack: 'fal',
    iconComponent: 'font-awesome-icon',
    modal: {
      canCancel: ['escape'],
      scroll: 'clip',
    },
    button: {
      rootClass: 'hiber-btn',
    },
    switch: {
      labelClass: 'o-field__label',
    },
    notification: {
      rootClass: 'h-notification',
    },
    loading: {
      iconClass: 'text-turquoise',
      iconSize: 'medium',
    },
  });

  app.component('FontAwesomeIcon', FontAwesomeIcon);
  app.component('PasswordMeter', PasswordMeter);
  app.component('HField', HField);
  app.component('VueDatePicker', VueDatePicker);

  app.mount('#app');
};

if (authMode === 'token') {
  initApp();
} else if (getAuthProviderName() === 'ory') {
  initApp();
} else {
  const firebase = initFirebase(firebaseConfig);
  const unsubscribe = getAuth(firebase).onAuthStateChanged(() => {
    unsubscribe();
    initApp();
  });
}
